import {
  forwardRef,
  RefObject,
  ReactElement,
  ReactNode,
  Ref,
  UIEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { SortFn, SortingState } from '@cast/design-system';
import { pseudoUnique, useDebounce } from '@cast/utils';

import { useCriteria } from './_hooks/useCriteria';
import { useFreeText } from './_hooks/useFreeText';
import { useSearchByCriteria } from './_hooks/useSearchByCriteria';
import { useSearchByFreeText } from './_hooks/useSearchByFreeText';
import { SearchContext } from './context';
import { UseServerData } from './server-side';
import { Criterion, FreeTextPredicate, SearchState } from './types';

export type SearchProviderProps<
  T,
  Filters extends Record<string, any> = Record<string, any>
> = {
  criteria?: Criterion[];
  serverSideSort?: boolean;
  urlKey?: string;
  disableFreeText?: boolean;
  freeTextServerSearch?: boolean;
  freeTextDebounceDelay?: number;
  onDataFiltered?: (data: any[]) => void;
  // Server side
  onServerValuesChanged?: (values?: Record<string, any>) => void;
  onServerSortingChanged?: (sortingState?: SortingState) => void;
  useData?: UseServerData<T, Filters>;
  // Client side
  data?: T[];
  error?: unknown;
  isLoading?: boolean;
  /** Client-side infinite scroll (e.g. cursor-paginated list accumulated locally). */
  isFetching?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  dataAggregator?: (data: T[]) => any[];
  freeTextPredicate?: FreeTextPredicate<T>;
  filterByFreeText?: (
    entries: T[],
    freeText: string,
    freeTextPredicate?: FreeTextPredicate<T>
  ) => T[];
  children?: ReactNode | ((searchState: SearchState<T>) => ReactNode);
};

const emptyCriteria: Criterion[] = [];

const InnerSearchProvider = <T,>(
  {
    criteria = emptyCriteria,
    serverSideSort,
    urlKey,
    disableFreeText,
    freeTextServerSearch,
    freeTextDebounceDelay = 200,
    onServerValuesChanged,
    onServerSortingChanged,
    onDataFiltered,
    useData,
    data,
    isLoading,
    isFetching: isFetchingProp,
    hasNextPage: hasNextPageProp,
    fetchNextPage: fetchNextPageProp,
    error,
    children,
    freeTextPredicate,
    dataAggregator,
    filterByFreeText,
  }: SearchProviderProps<T>,
  ref: Ref<SearchState>
) => {
  const {
    values,
    valuesChanged,
    setValue: _setValue,
    setValues,
    clearValue,
    resetInitial,
    clientValues,
    clientCriteria,
    serverCriteria,
    serverValues,
  } = useCriteria(criteria, urlKey);

  const setValue = _setValue;

  // Client
  const [sortFn, setSortFn] = useState<SortFn<T> | undefined>();

  // Server
  // null = "not yet sorted" — intentionally distinct from undefined (no sort).
  // getNoDataState checks `sortingState !== null` to decide whether to show the
  // no-data component; using null here prevents the DataTable from being hidden
  // on initial render before the default sort fires.
  const [sortingState, setSortingState] = useState<SortingState | undefined>(
    null as never
  );

  const { freeText, setFreeText } = useFreeText(urlKey);
  const debouncedFreeText = useDebounce(freeText, freeTextDebounceDelay);

  const clientSideFreeTextSearch = !disableFreeText && !freeTextServerSearch;
  const serverSideFreeTextSearch = !disableFreeText && freeTextServerSearch;

  const resultLoadingEnabled = !serverSideSort || sortingState !== null;
  const result = useData?.({
    values: serverValues,
    sortingState,
    freeText: serverSideFreeTextSearch ? debouncedFreeText : undefined,
    enabled: resultLoadingEnabled,
  });

  const hasDataAggregator = !!dataAggregator;
  const sortedData = useMemo(() => {
    const _data = result?.data || data;
    // If aggregator is provided, don't sort data until it is not filtered and aggregated
    return !serverSideSort && !hasDataAggregator && sortFn && _data
      ? sortFn(_data)
      : _data;
  }, [sortFn, data, result?.data, serverSideSort, hasDataAggregator]);

  const dataFilteredByCriteria = useSearchByCriteria(
    clientCriteria,
    clientValues,
    sortedData
  );

  const filteredData = useSearchByFreeText({
    data: dataFilteredByCriteria,
    freeText: debouncedFreeText,
    enabled: clientSideFreeTextSearch,
    freeTextPredicate,
    filterByFreeText,
  });

  const aggregatedData = useMemo(() => {
    if (!dataAggregator || !filteredData) {
      return filteredData;
    }
    const aggregatedData = dataAggregator(filteredData);
    // If aggregator is provided, sort data after filtering and aggregating
    if (sortFn) {
      return sortFn(aggregatedData);
    }
    return aggregatedData;
  }, [filteredData, dataAggregator, sortFn]);

  const [initialResetTrigger, setInitialResetTrigger] = useState<
    number | UIEvent | undefined
  >();

  useEffect(() => {
    onDataFiltered?.(aggregatedData || []);
  }, [aggregatedData, onDataFiltered]);

  useEffect(() => {
    onServerValuesChanged?.(serverValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverValues]);

  useEffect(() => {
    onServerSortingChanged?.(sortingState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortingState]);

  const _resetInitial = useCallback(
    (event?: UIEvent) => {
      resetInitial();
      setFreeText('');
      setInitialResetTrigger(event || pseudoUnique());
    },
    [resetInitial, setFreeText]
  );

  const hasLoadedResult = result && !result.isLoading && resultLoadingEnabled;
  const hasResultWithoutData = hasLoadedResult && !result?.data?.length;
  const noResultWithAppliedFilters = Boolean(
    hasResultWithoutData && result?.hasFiltersApplied
  );

  const content: ReactNode =
    typeof children === 'function' ? (
      <SearchContext.Consumer>
        {children as (state?: SearchState) => ReactNode}
      </SearchContext.Consumer>
    ) : (
      <>{children}</>
    );

  const searchState: SearchState = useMemo(
    () => ({
      data: data || result?.data,
      sortedData,
      filteredData: aggregatedData,
      isLoading: isLoading ?? result?.isLoading,
      isFetching: isFetchingProp ?? result?.isFetching,
      values,
      setValue,
      setValues,
      clearValue,
      freeText,
      initialText: freeText,
      debouncedFreeText,
      setFreeText,
      resetInitial: _resetInitial,
      valuesChanged: valuesChanged || !!freeText,
      initialResetTrigger,
      criteria,
      noResultWithAppliedFilters,
      // client
      clientCriteria,
      setSortFn: (fn) => setSortFn(() => fn),
      freeTextPredicate,
      filterByFreeText,
      dataAggregator,
      // server
      serverCriteria,
      serverSideSort,
      freeTextServerSearch,
      sortingState,
      setSortingState,
      urlKey,
      disableFreeText,
      hasPreviousPage: result?.hasPreviousPage,
      fetchPreviousPage: result?.fetchPreviousPage,
      hasNextPage: hasNextPageProp ?? result?.hasNextPage,
      fetchNextPage: fetchNextPageProp ?? result?.fetchNextPage,
      refetch: result?.refetch,
      error: error || result?.error,
      metadata: result?.metadata,
    }),

    [
      data,
      result?.data,
      sortedData,
      aggregatedData,
      isLoading,
      result?.isLoading,
      isFetchingProp,
      result?.isFetching,
      values,
      setValue,
      setValues,
      clearValue,
      freeText,
      debouncedFreeText,
      setFreeText,
      _resetInitial,
      valuesChanged,
      initialResetTrigger,
      criteria,
      noResultWithAppliedFilters,
      clientCriteria,
      freeTextPredicate,
      filterByFreeText,
      dataAggregator,
      serverCriteria,
      serverSideSort,
      freeTextServerSearch,
      sortingState,
      urlKey,
      disableFreeText,
      result?.hasPreviousPage,
      result?.fetchPreviousPage,
      hasNextPageProp,
      result?.hasNextPage,
      fetchNextPageProp,
      result?.fetchNextPage,
      result?.refetch,
      error,
      result?.error,
      result?.metadata,
    ]
  );

  useImperativeHandle(ref, () => searchState, [searchState]);

  return (
    <SearchContext.Provider value={searchState}>
      {content}
    </SearchContext.Provider>
  );
};

export const SearchProvider = forwardRef(InnerSearchProvider) as (<
  T,
  Filters extends Record<string, any> = Record<string, any>
>(
  props: SearchProviderProps<T, Filters> & {
    ref?: Ref<SearchState<T>> | RefObject<SearchState<T>>;
  }
) => ReactElement) & { displayName: string };
SearchProvider.displayName = 'SearchProvider';
